import {
    Autocomplete,
    Box,
    TextField,
    Typography,
    Paper,
    Divider, Grid, Chip
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {Person, Build, Help} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import {useAppContext} from "../../../../../contexts/AppContext";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export function NewRequisicaoFirstStep({
                                           isLoading,
                                           pessoas,
                                           equipamentos,
                                           selectedPessoa,
                                           setSelectedPessoa,
                                           selectedEquipamento,
                                           setSelectedEquipamento,
                                           selectedEmpresa,
                                           setNextStep,
                                       }) {
    const { setEquipamentoIdSession, getEquipamentoIdSession } = useAppContext();
    const [timeoutReached, setTimeoutReached] = useState(false);

    const isSelectionRequired = Boolean(
        selectedEmpresa?.equipmentSelectionRequired
    );

    const isInvalidSelection =
        isSelectionRequired &&
        (!selectedEquipamento || selectedEquipamento.id === 0);

    const equipamentosComOpcao = useMemo(() => {
        if (isSelectionRequired) {
            return equipamentos || [];
        }

        return [
            ...(equipamentos || []),
        ];
    }, [equipamentos, isSelectionRequired]);

    const equipamentoIdSalvo = getEquipamentoIdSession();

    const equipamentoSalvo =
        equipamentosComOpcao.find((e) => e.id === equipamentoIdSalvo) ||
        equipamentosComOpcao[0];

    useEffect(() => {
        if (equipamentoSalvo && equipamentoSalvo.id !== selectedEquipamento?.id) {
            setSelectedEquipamento(equipamentoSalvo);
        }
    }, [equipamentoSalvo]);

    useEffect(() => {
        setNextStep(selectedPessoa && selectedEquipamento);
    }, [selectedPessoa, selectedEquipamento, setNextStep]);

    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                setTimeoutReached(true);
            }, 10000);

            return () => clearTimeout(timer);
        } else {
            setTimeoutReached(false);
        }
    }, [isLoading]);

    return (
        <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        border: selectedPessoa ? '2px solid #1E5139' : '1px solid #e0e0e0',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Person sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Colaborador
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Busque pelo nome ou matrícula do colaborador que realizará a requisição
                    </Typography>

                    <Autocomplete
                        options={pessoas}
                        getOptionLabel={(option) => `${option.matricula} - ${option.nome}`}
                        getOptionDisabled={(option) => !option.status}
                        value={selectedPessoa || null}
                        onChange={(event, newValue) => {
                            setSelectedPessoa(newValue);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Selecione o colaborador"
                                placeholder="Digite para buscar..."
                                variant="outlined"
                                fullWidth
                            />
                        )}
                        disabled={isLoading || pessoas.length === 0}
                        noOptionsText={
                            isLoading && !timeoutReached
                                ? "Carregando..."
                                : "Nenhum colaborador disponível"
                        }
                    />
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        border: selectedEquipamento ? '2px solid #1E5139' : '1px solid #e0e0e0',
                        transition: 'all 0.3s ease'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Build sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Equipamento
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Selecione o equipamento ou EPI que será requisitado
                    </Typography>

                    <Autocomplete
                        options={equipamentosComOpcao}
                        getOptionLabel={(option) => option.nome || option.titulo || ""}
                        value={selectedEquipamento || equipamentosComOpcao[0]}
                        onChange={(event, newValue) => {
                            if (isSelectionRequired && (!newValue || newValue.id === 0)) {
                                return;
                            }

                            if (!newValue || newValue.id === 0) {
                                setSelectedEquipamento(equipamentosComOpcao[0]);
                                localStorage.removeItem("@STW:idEquipamentoSelected");
                            } else {
                                setSelectedEquipamento(newValue);
                                localStorage.setItem("@STW:idEquipamentoSelected", newValue.id);
                            }
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    isSelectionRequired
                                        ? "⚠️ SELEÇÃO OBRIGATÓRIA — EQUIPAMENTO"
                                        : "Selecione o equipamento"
                                }
                                placeholder="Escolha com atenção..."
                                variant="outlined"
                                fullWidth
                                error={isInvalidSelection}
                                helperText={
                                    isInvalidSelection
                                        ? "🚨 Você deve selecionar um equipamento antes de continuar"
                                        : isSelectionRequired
                                            ? "Atenção: esta escolha impacta diretamente a operação"
                                            : ""
                                }
                                sx={{
                                    backgroundColor:
                                        isInvalidSelection ? "#fff5f5" : "inherit",

                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        fontWeight: 600,

                                        "& fieldset": {
                                            borderColor: isInvalidSelection ? "#d32f2f" : undefined,
                                            borderWidth: isInvalidSelection ? 2 : 1,
                                        },

                                        "&:hover fieldset": {
                                            borderColor: isInvalidSelection ? "#b71c1c" : undefined,
                                        },

                                        "&.Mui-focused fieldset": {
                                            borderColor: isInvalidSelection ? "#b71c1c" : undefined,
                                            borderWidth: 2,
                                        },
                                    },
                                }}
                            />
                        )}
                        disabled={isLoading || equipamentosComOpcao.length === 0}
                        noOptionsText="Nenhum equipamento disponível"
                    />
                    {selectedEquipamento && selectedEquipamento.id !== 0 && (
                        <Box
                            sx={{
                                mt: 1,
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: "rgb(232,245,233)",
                                border: "1px solid #1E5139",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <CheckCircleIcon sx={{ color: "primary.main" }} />

                            <Typography sx={{ fontWeight: 600 }}>
                                Equipamento selecionado:
                            </Typography>

                            <Typography sx={{ fontWeight: 700 }}>
                                {selectedEquipamento.nome || selectedEquipamento.titulo}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>

            <Box sx={{ mt: 4 }}>
                {(selectedPessoa && selectedEquipamento) ? (
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#E8F5E9',
                                border: '1px solid #4CAF50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 22 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1B5E20',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                Seleção realizada. Clique em <strong>"Continuar"</strong> para continuar.
                            </Typography>
                        </Paper>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFB300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <HourglassBottomIcon sx={{ color: '#FB8C00', fontSize: 22 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#E65100',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                Aguardando seleção...
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Box>
        </Box>
    );
}